import { NgStyle } from "@angular/common";
import {
    Component,
    NgZone,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { PageComponent } from "@components/page/page.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import { VerticalCollapsableComponent } from "@components/vertical-collapsable/vertical-collapsable.component";
import { Subject, Subscription, merge } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ITab } from "~app/common/components/tabs/tab/itab";
import { ToastService } from "~app/common/components/toast/toast.service";
import { CanBeDirty } from "~app/common/guards/dirty.guard";
import { ConfirmDialogResponse, RaskRoller, WebInstallning } from "~app/common/models/constants";
import { ArbetsorderNameInfoDTO } from "~app/common/models/planera/v2/ArbetsorderNameInfoDTO";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { IBridgeEvent } from "~app/common/services/event-bridge.service";
import { FeatureService } from "~app/common/services/feature.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { Barighet } from "~app/features/planera/model/Barighet";
import { GetArbetsorderRequest } from "~app/features/planera/model/GetArbetsorderRequest";
import { IGetArbetsorderRequest } from "~app/features/planera/model/IGetArbetsorderRequest";
import { IPlaneraBasdataResponse } from "~app/features/planera/model/IPlaneraBasdataResponse";
import { ArbetsorderPlaneringSchedulerComponent } from "./arbetsorder-planering-scheduler/arbetsorder-planering-scheduler.component";
import { ArbetsorderSearchHybridService } from "./arbetsorder-search/arbetsorder-search-hybrid.service";
import { ArbetsorderSearchComponent } from "./arbetsorder-search/arbetsorder-search.component";
import { ArbetsorderSparaComponent } from "./arbetsorder-spara/arbetsorder-spara.component";
import { ListEvents } from "./events/list-events";
import { MapEvents } from "./events/map-events";
import { Origin } from "./events/origin";
import { IMouseOverArbetsorderChangedEvent, IPlaneraTheaterView, PlaneraEvents } from "./events/planera-events";
import { IArbetsorderDetail, IAtgardBarighetChanged } from "./planera-detail/planera-detail-models";
import { PlaneraDetailComponent } from "./planera-detail/planera-detail.component";
import { PlaneraKartaEventService } from "./planera-karta/planera-karta-event.service";
import { PlaneraKartaComponent } from "./planera-karta/planera-karta.component";
import { PlaneraSearchResultsComponent } from "./planera-search-results/planera-search-results.component";
import { IPlaneraTab, PlaneraTabsComponent } from "./planera-tabs/planera-tabs.component";
import { ProduktionsmalPlanComponent } from "./produktionsmal/produktionsmal-plan.component";
import { ArbetsorderMediatorService } from "./services/arbetsorder-mediator.service";
import { PlaneraBridgeEvents, PlaneraEventService } from "./services/planera-event.service";
import { PlaneraService } from "./services/planera.service";
import { SkapaArbetsorderDialogComponent } from "./skapa-arbetsorder-dialog/skapa-arbetsorder-dialog.component";

@Component({
    selector: "rask-planera",
    templateUrl: "./planera.component.html",
    styleUrls: ["./planera.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        BlockComponent,
        ProduktionsmalPlanComponent,
        ArbetsorderSearchComponent,
        NgStyle,
        ButtonComponent,
        TabsComponent,
        VerticalCollapsableComponent,
        PlaneraSearchResultsComponent,
        PlaneraDetailComponent,
        PlaneraTabsComponent,
        PlaneraKartaComponent,
        ArbetsorderPlaneringSchedulerComponent,
        ArbetsorderSparaComponent
    ]
})
export class PlaneraComponent implements OnInit, OnDestroy, CanBeDirty {
    readonly arbetsorderSearchHybridService = inject(ArbetsorderSearchHybridService);

    aoSearchFilter: IGetArbetsorderRequest;
    aoTabs = [
        {
            label: "Sökresultat",
            lockedTab: true,
            tabId: null,
            routeId: null
        }
    ] as ITab[];
    basdata: IPlaneraBasdataResponse;
    currentTabId = null;
    hasRoleArbetsordersystemadmin = false;
    hasRoleProduktionsleda = false;
    hoveredBeteckning = null;
    mapTheatherModeEnabled = false;
    maxTabs = 9;
    mode = "karta";
    ngxPlaneraHoverClass = "hovering";
    @ViewChildren(PlaneraDetailComponent) planeraChildren: QueryList<PlaneraDetailComponent>;
    planeratabs = [
        {
            id: "karta",
            name: "Karta",
            hidden: false
        },
        {
            id: "schema",
            name: "Schema",
            hidden: false
        },
        {
            id: "malPlan",
            name: "Mål & Plan",
            hidden: true
        }
    ];
    resultTab = "searchResult";
    updatedAtgardBarighet: IAtgardBarighetChanged[];
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly arbetsorderMediatorService = inject(ArbetsorderMediatorService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly featureService = inject(FeatureService);
    private readonly ngZone = inject(NgZone);
    private readonly planeraBridgeEvents: IBridgeEvent[] = [];
    private readonly planeraEventService = inject(PlaneraEventService);
    private readonly planeraKartaEventService = inject(PlaneraKartaEventService);
    private readonly planeraKartaEvents: IBridgeEvent[] = [];
    private readonly planeraService = inject(PlaneraService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly router = inject(Router);
    private readonly subscriptions: Subscription[] = [];
    private readonly toasterService = inject(ToastService);
    private readonly unSubscribe$ = new Subject<void>();
    private readonly webInstallningService = inject(WebinstallningarService);

    addAOTab(aoid, aoNameInfo: ArbetsorderNameInfoDTO): boolean {
        if (this.aoTabs.length >= this.maxTabs) {
            this.toasterService.add(
                `Du kan maximalt ha ${this.maxTabs - 1} arbetsordrar öppna samtidigt. Stäng en arbetsorder för att kunna öppna en ny.`,
                2,
                6000);
            return false;
        }

        if (!this.aoTabs.find(x => x.routeId === aoid)) {
            const newTab = {
                tabId: aoid,
                label: aoNameInfo.beteckning,
                tooltipText: aoNameInfo.namn,
                routeId: aoid.toString(),
                deactivate: this.canDeactivate(aoid),
                canDeactivate: this.canDeactivate(aoid)
            } as ITab;

            this.aoTabs.push(newTab);
            this.aoTabs = [].concat(this.aoTabs);
        }
        return true;
    }

    canDeactivate(aoid: any): () => Promise<boolean> {
        return () => new Promise<boolean>(resolve => {
            const foundChild = this.planeraChildren.find(child => child.arbetsorderid === aoid);
            if (foundChild) {
                return foundChild.isDirtyFn().then(success => resolve(success));
            }
            return resolve(true);
        });
    }

    closeAOTab(aoTab: ITab) {
        aoTab.canDeactivate().then((result) => {
            if (result) {
                if (this.currentTabId === aoTab.tabId) {
                    this.gotoSearchResultView();
                }
                this.aoTabs = this.aoTabs.filter(x => x.tabId !== aoTab.tabId);
            }
        });
    }

    closeAllAOTabs() {
        if (this.currentTabId) {
            const selectedAOTab = this.aoTabs.find(tab => tab.tabId === this.currentTabId);
            selectedAOTab.canDeactivate().then((result) => {
                if (result) {
                    this.gotoSearchResultView();
                    this.aoTabs = this.aoTabs.filter(x => x.lockedTab);
                }
            });
        } else {
            this.gotoSearchResultView();
            this.aoTabs = this.aoTabs.filter(x => x.lockedTab);
        }
    }

    create = (): void => {
        this.openDialog();
    };

    emitMouseOverAOTab(tab: ITab, isMouseover: boolean) {
        if (!tab.tabId || !tab.label) {
            return;
        }
        this.planeraKartaEventService.emit(PlaneraEvents.mouseOverArbetsorderTab, {
            beteckning: tab.label,
            isMouseover: isMouseover
        });
    }

    getAOTabs() {
        return this.aoTabs.filter(x => x.tabId !== null);
    }

    gotoSearchResultView() {
        this.router.navigate([
            "planera",
            ""
        ]);
    }

    initBasdata() {
        this.webInstallningService.getAsync<IGetArbetsorderRequest>(WebInstallning.PlaneraFilter).then(filter => {
            const planeraFilter = new GetArbetsorderRequest();
            const webInstFilterObj = filter;
            Object.assign(planeraFilter, webInstFilterObj);
            this.aoSearchFilter = planeraFilter;
            this.arbetsorderSearchHybridService.triggerSetFilter(planeraFilter);
        });

        this.busyService.addWork(this.planeraService.getPlaneraBasdata()).subscribe((response) => {
            this.basdata = response;
            this.basdata.barigheter = [
                new Barighet(65, "A"),
                new Barighet(66, "B"),
                new Barighet(67, "C"),
                new Barighet(68, "D")
            ];
        });
    }

    isDirtyFn(): Promise<boolean> {
        const selectedAOTab = this.aoTabs.find(tab => tab.tabId === this.currentTabId);
        if (!selectedAOTab || !selectedAOTab.canDeactivate) {
            return Promise.resolve(true);
        }
        return selectedAOTab.canDeactivate();
    }

    ngOnDestroy(): void {
        this.arbetsorderMediatorService.destroy();
        this.planeraKartaEventService.unsubscribeMany(this.planeraKartaEvents);
        this.planeraEventService.unsubscribeMany(this.planeraBridgeEvents);
        this.subscriptions.forEach(s => s.unsubscribe());
        this.subscriptions.splice(0);
        this.unSubscribe$.next();
        this.unSubscribe$.complete();
    }

    ngOnInit(): void {
        this.initBasdata();

        merge(this.activatedRoute.params).pipe(
            takeUntil(this.unSubscribe$)
        ).subscribe(() => this.handleStateChange());

        this.setupEvents();
        this.updatedAtgardBarighet = [];

        this.hasRoleArbetsordersystemadmin = this.authenticationService.hasRole(RaskRoller.role.Arbetsordersystemadmin);
        this.hasRoleProduktionsleda = this.authenticationService.hasRole(RaskRoller.role.Produktionsleda);
    }

    ngShow(status: boolean) {
        // eslint-disable-next-line sonarjs/no-selector-parameter
        if (status) {
            return "block";
        } else {
            return "none";
        }
    }

    noCurrentTab() {
        return this.currentTabId === null;
    }

    onHoverAO(payload: IMouseOverArbetsorderChangedEvent) {
        this.hoveredBeteckning = (payload.isMouseover ? payload.beteckning : null);
        const element = document.getElementById(payload.beteckning);
        if (!element) {
            return;
        }
        element.classList.remove(this.ngxPlaneraHoverClass);
        if (this.hoveredBeteckning) {
            element.classList.add(this.ngxPlaneraHoverClass);
        }
    }

    removeAoTab(aoid: number) {
        const tabIndex = this.aoTabs.findIndex(tab => tab.tabId && tab.tabId === aoid);
        if (tabIndex > -1) {
            this.aoTabs.splice(tabIndex, 1);
            this.aoTabs = [].concat(this.aoTabs);
            if (this.currentTabId === aoid) {
                this.gotoSearchResultView();
            }
        }
    }

    resultTabChanged($event: IPlaneraTab) {
        if (this.resultTab === $event.id) {
            return;
        }
        this.resultTab = $event.id;
    }

    saveFilter() {
        this.webInstallningService.set(WebInstallning.PlaneraFilter, this.aoSearchFilter);
    }

    setupEvents() {
        this.arbetsorderMediatorService.reset();

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe(MapEvents.mouseOverArbetsorder, (payload: IMouseOverArbetsorderChangedEvent) => {
            this.onHoverAO(payload);
        }));

        this.subscriptions.push(this.arbetsorderSearchHybridService.searchResultsUpdated
            .subscribe((aolist) => {
                this.saveFilter();
                if (this.currentTabId) {
                    const selectedAOTab = this.aoTabs.find(tab => tab.tabId === this.currentTabId);
                    selectedAOTab.canDeactivate().then((result) => {
                        if (result) {
                            this.gotoSearchResultView();
                        }
                    });
                } else {
                    this.gotoSearchResultView();
                }
            }));

        this.arbetsorderMediatorService.arbetsorder
            .pipe(takeUntil(this.unSubscribe$))
            .subscribe(changeEvent => {
                this.updateTabTooltip(changeEvent?.arbetsorderWrapper?.arbetsorder);
            });

        this.planeraBridgeEvents.push(this.planeraEventService.subscribe(PlaneraBridgeEvents.arbetsorderProbablyDeleted, (data: { arbetsorderId: number }) => {
            this.planeraService.doesArbetsorderExist(data.arbetsorderId).subscribe(exists => {
                if (exists === false) {
                    this.planeraEventService.emit(PlaneraBridgeEvents.arbetsorderDeleted, { arbetsorderId: data.arbetsorderId });
                }
            });
        }));

        this.planeraBridgeEvents.push(this.planeraEventService.subscribe(PlaneraBridgeEvents.arbetsorderDeleted, (data: { arbetsorderId: number }) => {
            this.removeAoTab(data.arbetsorderId);
            this.planeraKartaEventService.emit(MapEvents.arbetsorderDeleted, data.arbetsorderId);
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe<IPlaneraTheaterView>(PlaneraEvents.theaterView, (payload) => {
            this.toggleMapTheaterMode(payload.theaterViewActive, false);
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe(PlaneraEvents.mouseOverArbetsorderChanged, (payload: IMouseOverArbetsorderChangedEvent) => {
            // Endast arbetsorder-header publicerar detta event via planeraKartaEventService för tillfället
            if (payload.origin === Origin.listHeader) {
                const event = ListEvents.mouseOverArbetsorder;
                this.planeraKartaEventService.emit(event, {
                    beteckning: payload.beteckning,
                    isMouseover: payload.isMouseover,
                    origin: Origin.list
                });
            }

            this.onHoverAO(payload);
        }));
    }

    tabChanged($event: IPlaneraTab) {
        this.mode = $event.id;
    }

    toggleMapTheaterMode(forcedState: boolean, publishEvent: boolean) {
        this.mapTheatherModeEnabled = forcedState;
        if (publishEvent) {
            this.planeraKartaEventService.emit(PlaneraEvents.theaterView, { theaterViewActive: this.mapTheatherModeEnabled });
        }
    }

    updateTabTooltip(ao: IArbetsorderDetail) {
        if (ao) {
            const aoTab = this.aoTabs.find(x => x.tabId === ao.id);
            if (aoTab) {
                aoTab.tooltipText = ao.namn;
            }
        }
    }

    visaSkapaArbetsorder(): boolean {
        return this.hasRoleArbetsordersystemadmin || this.hasRoleProduktionsleda;
    }

    private getArbetsorderIDFromQueryString(): number | null {
        const aoid = +this.activatedRoute.snapshot.paramMap.get("id");
        return aoid > 0 ? aoid : null;
    }

    private handleStateChange() {
        this.ngZone.run(() => {
            const stateid = this.getArbetsorderIDFromQueryString();
            const existingAOTab = this.aoTabs.find(x => x.tabId === stateid);
            if (!existingAOTab) {
                this.busyService.addWork(this.planeraService.getArbetsorderNameInfo(stateid))
                    .subscribe(aoNameInfo => {
                        if (this.addAOTab(stateid, aoNameInfo)) {
                            this.currentTabId = stateid;
                        } else {
                            this.gotoSearchResultView();
                        }
                    });
            } else {
                this.aoTabs = [].concat(this.aoTabs);
                this.currentTabId = stateid;
            }
            if (!stateid) {
                this.toggleVisibilityMalPlanTab(false);
                this.planeraKartaEventService.emit(MapEvents.selectArbetsorder, null);
                this.arbetsorderMediatorService.setArbetsorder(null);
            } else {
                this.toggleVisibilityMalPlanTab(true);
            }
        });
    }

    private openDialog() {
        this.raskModalService.openAsync("Skapa arbetsorder", SkapaArbetsorderDialogComponent, "xl", true,null).subscribe(x => {
            if (x !== ConfirmDialogResponse.cancel) {
                this.router.navigate([
                    "planera",
                    x
                ]);
            }
        });
    }

    private toggleVisibilityMalPlanTab(show: boolean) {
        if (this.mode === "malPlan") {
            this.mode = "karta";
        }
        const tab = this.planeratabs.find(t => t.id === "malPlan");
        tab.hidden = !show;
    }
}
